import { LanguagesEnum } from '@/api/enums';
import { ListingRequest, ListingResponse } from '@/api/types';
import { ContentSeries, Course, CourseScheduleSource } from '@/client/courses';
import { File } from '@/client/files';
import { Tag } from '@/client/tags';
import { TargetEntities, TargetEntitiesRequest } from '@/common/types';

export enum PlannerType {
  TRAINING_PLAN = 'training',
  ONBOARDING_PLAN = 'onboarding',
}

export enum PlannerTemplateType {
  RECOMMENDED = 'recommended',
  BLANK = 'blank',
}

export enum CourseSchedulePeriodUnit {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
}

export type EnrollOffset = {
  value: number;
  unit: CourseSchedulePeriodUnit;
};

export type PlannerCRUDCourse = {
  id: string;
  name?: string;
  thumbnailUrl?: string;
  courseThumbnail?: File;
  enrollDate?: Date;
  enrollmentHour?: number;
  enrollImmediately?: boolean;
  releaseDate?: Date | null;
  plannerScheduleDate?: Date | null;
  executedAt?: Date;
  periodValue?: number;
  periodUnit?: CourseSchedulePeriodUnit;
  dateKey?: string;
  isDateHeader?: boolean;
  isPastDate?: boolean;
  isLastOfDateGroup?: boolean;
  firstNonPastDate?: boolean;
  isUpcoming?: boolean;
  source?: CourseScheduleSource;
  autoBaseline?: string;
  skipUsersEnrolledWithinGracePeriod: boolean;
  positionInPlanner?: number;
  contentSeries?: ContentSeries;
};

export type SavePlannerRequest = {
  id?: string;
  name: string;
  targets: TargetEntitiesRequest;
  courses: PlannerCRUDCourse[];
  excludes: TargetEntitiesRequest;
  accountId: string;
  type: PlannerType;
  autoEnroll?: boolean;
  subscribedSeries?: ContentSeries[];
  isActive?: boolean;
};

export type DeletePlannerRequest = {
  accountId: string;
  planId: string;
};

export type AddCoursePlannersRequest = {
  courses: string[];
  date: Date;
};

export type PlannerCourseSchedule = {
  course: Course;
  enrollDate?: Date;
  enrollmentHour?: number;
  enrollImmediately?: boolean;
  executedAt?: Date;
  periodValue?: number;
  periodUnit?: CourseSchedulePeriodUnit;
  source?: CourseScheduleSource;
};

export type CoursePlanner = {
  id: string;
  name: string;
  dynamicCourseSchedules: PlannerCourseSchedule[];
  excludes: TargetEntities;
  targets: TargetEntities;
  staticCourseSchedules: PlannerCourseSchedule[];
  editable?: boolean;
  type: PlannerType;
  autoEnroll?: boolean;
  subscribedSeries?: ContentSeries[] | null;
  isActive?: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type TargetSelectorPreviewRequest = {
  targets?: TargetEntitiesRequest;
  excludes?: TargetEntitiesRequest;
  domainVerifiedOnly?: boolean;
};

export type TargetSelectorPreviewResponse = {
  usersCount: number;
  excludedDomainUnverifiedCount: number;
};

export type ListCoursePlannerRequest = ListingRequest;
export type ListCoursePlannerResponse = ListingResponse<CoursePlanner>;

export type PlannerCoursesFiltersType = {
  tags?: Tag[];
  series?: (ContentSeries | null)[];
  courseLanguages?: LanguagesEnum[];
  search?: string;
};
